import {
    Component,
    ContentChild,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    TemplateRef,
    ViewChild
} from '@angular/core';
import {UntypedFormControl, UntypedFormGroup} from '@angular/forms';
import {BehaviorSubject, ReplaySubject, Subject} from 'rxjs';
import {take, takeUntil} from 'rxjs/operators';
import {SelectFilterElement} from '../../../../components/common/select-filter/select-filter.model';
import {MatSelect, MatSelectChange} from '@angular/material/select';
import {ContentDirective} from '../../../../shared/directives/content.directive';
import {ContentModalDirective} from '../../../../shared/directives/content-modal.directive';
import {AbstractSelectComponent, NewItemModalOptions} from '../select/abstract-select.component';


@Component({
    selector: 'app-multiple-select-filter',
    templateUrl: './multiple-select-filter.component.html',
    styleUrls: ['./multiple-select-filter.component.scss'],
})
export class MultipleSelectFilterComponent<V, E> extends AbstractSelectComponent<V> implements OnInit, OnDestroy {

    @ContentChild(ContentDirective, {static: true, read: TemplateRef}) template: TemplateRef<any>;
    @ContentChild(ContentModalDirective, {static: true, read: TemplateRef}) templateNewItem: TemplateRef<any>;

    @Input() parentFormGroup: UntypedFormGroup;
    @Input() noEntriesFoundLabel: string;
    @Input() placeHolderLabel: string;
    @Input() parentFormControlName: string;
    @Input() elements: BehaviorSubject<SelectFilterElement<V, E>[]> = new BehaviorSubject<SelectFilterElement<V, E>[]>([]);
    @Input() filterElementProcessor: (criteria: string, element: any) => boolean;
    @Input() loading = false;
    @Input() newItemModalOptions: NewItemModalOptions<any, any, any>;
    @Input() createButtonLabel: string;

    @Output() selectionChangedEvent = new EventEmitter<any[]>();
    @Output() focussedEvent: EventEmitter<void> = new EventEmitter<void>();

    @ViewChild('matSelect') matSelect: MatSelect;
    constructor() {
        super();
    }

    /** control for filter */
    filterCtrl: UntypedFormControl = new UntypedFormControl();
    /** Subject that emits when the component has been destroyed. */
    _onDestroy = new Subject<void>();
    filteredElements: ReplaySubject<SelectFilterElement<V, E>[]> = new ReplaySubject<SelectFilterElement<V, E>[]>(1);
    list: SelectFilterElement<V, E>[] = [];
    dialogId;

    @ViewChild('matSelect', {static: true}) multiSelect: MatSelect;

    onSelectionChanged(event: MatSelectChange) {
        const values: V[] = event.value;
        const elements: SelectFilterElement<V, E>[] = [];
        values.forEach(value => {
            elements.push(this.list.find(element => element.value === value));
        });
        // console.log([elements, values]);
        this.selectionChangedEvent.emit(elements.map(value => value.id));
    }


    ngOnInit() {
        this.elements
            .pipe(takeUntil(this._onDestroy))
            .subscribe(
                (value: any[]) => {
                    this.list = value;
                    this.filteredElements.next(this.list);
                }
            );
        this.filterCtrl.valueChanges
            .pipe(takeUntil(this._onDestroy))
            .subscribe(criteria => {
                this.filter(criteria);
            });
    }

    private filter(criteria: string) {
        if (!this.elements) {
            return;
        }
        if (!criteria) {
            this.filteredElements.next(this.list);
            return;
        }
        this.filteredElements.next(
            this.list.filter(element => this.filterElements(criteria, element.element))
        );
    }

    filterElements = (criteria: string, element: E) => {
        if (this.filterElementProcessor) {
            return this.filterElementProcessor(criteria, element);
        } else {
            return element.toString().replace(/<[^>]*>/g, '').toUpperCase().includes(criteria.toUpperCase());
        }
    }

    ngOnDestroy() {
        this._onDestroy.next();
        this._onDestroy.complete();
    }

    get elementControl() {
        return this.parentFormGroup.get(this.parentFormControlName);
    }

    toggleSelectAll(selectAllValue: boolean) {
        this.filteredElements.pipe(take(1), takeUntil(this._onDestroy))
            .subscribe(val => {
                    if (selectAllValue) {
                        this.elementControl.patchValue(val.map(value => value.value));
                    } else {
                        this.elementControl.patchValue([]);
                    }
                }
            );
    }

    getSelectedElements(): E[] {
        if (this.elementControl.value && this.elements && this.elements.getValue()) {
            // console.log(['elementControl.value', this.elementControl.value, this.elements.getValue()]);
            return this.elements.getValue()
                .filter(item => this.elementControl.value.find(value => value === item.value))
                .map(item => item.element);
        }
        return undefined;
    }

    get filterInput(): string {
        return this.filterCtrl.value;
    }

    focussed(){
        this.focussedEvent.emit();
    }

    addNewItem(value: any) {
        if (value) {
            const values: any[] = this.elementControl.value?.filter(elementValue => elementValue);
            console.log(['addNewItem', value, values]);
            if (values.findIndex(value1 => value1 === value) === -1) {
                console.log('addNewItem not in values');
                values.push(value);
                this.elementControl.setValue(values);
            }
        }
    }

    closeSelect() {
        this.matSelect.close();
    }

    getSelectInputValue(): string {
        return this.filterCtrl.value;
    }
}
